import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { GridRegistrationRequestService } from '@bo-client/activation-management/grid-registration/grid-registration-request.service';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { GridRegistrationFormBase } from '@bo-client/activation-management/grid-registration/grid-registration-form.base';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  GridRegistrationStatusEnum,
  GridRegistrationTypeEnum
} from '../../../grid-registration/model/grid-registration.enum';
import { BehaviorSubject, combineLatest, Observable, of, switchMap } from 'rxjs';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import {
  AreaTypeEnum,
  CommonStatusEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { ConsumerModel } from '@bo-client/activation-management/grid-registration/model/consumer.model';
import { combineLatestWith, filter, map, shareReplay, startWith, takeUntil, tap } from 'rxjs/operators';
import { ContactDataDto } from '@bo-client/shared/models/contactDataDto';
import { CodeListValue } from '@bo-client/shared/models/codeListValue';
import { MetaService } from '@bo-client/core/api/meta-service';
import { ConnectionObjectFormV2 } from '@bo-client/shared/models/connectionObject';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { UiAdaptionsModel } from '@bo-client/tenant-management/models/ui-adaptions.model';

@Component({
  selector: 'app-grid-registration-form-consumer',
  templateUrl: './grid-registration-form-consumer.component.html',
  styleUrls: ['./grid-registration-form-consumer.component.scss']
})
export class GridRegistrationFormConsumerComponent extends GridRegistrationFormBase implements OnInit {
  private destroyRef = inject(DestroyRef);
  private validatorsService = inject(ValidatorsService);
  private modelState$ = new BehaviorSubject<CommonStatusEnum | null>(null);

  @Input() public originalModel: GridRegistrationModel;

  public formGroup: FormGroup;

  canUpdate$: Observable<boolean>;
  disableAll$: Observable<boolean>;

  protected readonly GridRegistrationTypeEnum = GridRegistrationTypeEnum;

  selectedCompanyLocation$: Observable<CompanyLocationDto>;

  protected readonly AreaTypeEnum = AreaTypeEnum;

  // Computed properties for the form fields
  get totalConsumption(): number {
    const existing = this.formGroup.controls['existingConsumption'].value;
    const newCons = this.formGroup.controls['newConsumption'].value;
    return existing + newCons;
  }

  get differingInvoiceContact() {
    return this.formGroup.get('differingInvoiceContact')?.value;
  }

  // Für die Verwendung von form arrays
  get uploads(): FormArray {
    return this.formGroup.get('uploads') as FormArray;
  }

  get consumers(): FormArray {
    return this.formGroup.get('consumers') as FormArray;
  }

  get gridRegistrationType(): GridRegistrationTypeEnum {
    return this.originalModel?.gridRegistrationType;
  }

  get documentsUploadInfoUIAdaptionName() {
    return 'gridRegistration.' + this.gridRegistrationType.valueOf() + '.documentsUploadInfo';
  }
  get conditionsCheckboxUIAdaptionName(): string | null {
    if (
      ![
        GridRegistrationTypeEnum.EHeating,
        GridRegistrationTypeEnum.Charger,
        GridRegistrationTypeEnum.Misc,
        GridRegistrationTypeEnum.Storage
      ].includes(this.gridRegistrationType)
    ) {
      return null;
    }
    return `gridRegistration.${this.gridRegistrationType.valueOf()}.conditionsCheckbox`;
  }
  get firstConsumer(): FormGroup {
    return (this.formGroup.get('consumers') as FormArray)?.controls[0] as FormGroup;
  }

  get customer(): FormGroup {
    return this.formGroup.get('customer') as FormGroup;
  }

  get invoiceContact(): FormGroup {
    return this.formGroup.get('invoiceContact') as FormGroup;
  }

  get acceptConditions() {
    return this.formGroup.controls['acceptConditions'] as FormGroup;
  }

  get meteringConcept() {
    return this.formGroup.get('meteringConcept.id') as FormControl;
  }

  get actionTypeValue() {
    return this.formGroup.get('actionType')?.value;
  }

  get existingFeeders(): FormArray {
    return this.formGroup.get('existingFeeders') as FormArray;
  }

  getFormGroup(): FormGroup {
    return this.formGroup;
  }

  hideAdditionalGridConnectionPointInformationCard$: Observable<boolean>;
  customConditionsCheckbox = new FormControl(false);

  constructor(
    protected authorizationService: AuthorizationService,
    protected httpHapService: HttpHapService,
    protected dataShareService: DataShareService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected toastService: ToastrService,
    protected errorsFormsService: ErrorsFormsService,
    private fb: FormBuilder,
    private createFormService: CreateFormsService,
    protected gridRegistrationService: GridRegistrationRequestService,
    protected createFormsGridRegistrationService: CreateGridRegistrationFormsService,
    private metaService: MetaService
  ) {
    super(authorizationService, dataShareService);

    // TODO: move this into a dedicated service!?
    this.formGroup = this.fb.group({
      state: [''],
      formVersion: ['v2'],

      comment: [null],
      shortDescription: [null],
      longDescription: [null],

      installerFeedbackComment: [null],

      gridConnectionPointId: [null],
      externalId: [null],
      assignedTransformerStation: [null],

      trId: [null],
      srId: [null],
      meLo: [null],
      maLo: [null],

      customer: this.createFormService.createContactDataFormV2({}, true),
      invoiceContact: this.createFormService.createContactDataFormV2({}, false),
      differingInvoiceContact: [false], // (Transient) steuert ob ein abweichender Rechnungsempfänger/Anschlussnehmer vorhanden ist.

      // Anschlussobject
      connectionObject: this.createFormService.connectionObjectFormWithValidatorV2({}),
      connectionObjectAdditionalInfo: this.createFormService.connectionObjectAdditionalInfoForm({}),

      // Der Betriebsbereich
      companyLocation: {
        id: [null]
      },

      extractionNetworkPowerAuthorized: [null],
      actionType: [null],

      consumers: this.fb.array([
        this.createFormsGridRegistrationService.createConsumerFormV2(new ConsumerModel(null))
      ]),
      meteringConcept: this.fb.group({ id: [null] }),
      uploads: this.fb.array([]), // Dokumente

      // Bedingungen und Erklärungen akzeptiert
      acceptedConditions1: [null, Validators.required],

      // Weitere Informationen zum Netzanschluss
      contractedConnectionPower: [null],
      connectionFuseAmplitude: [null],
      hasInstalledDevices: [null],
      existingFeeders: this.fb.array([])
    });
    this.initConnectionObjectData$();

    this.hideAdditionalGridConnectionPointInformationCard$ = this.dataShareService.tenantConfig$.pipe(
      map((config) => config?.gridRegistration?.hideAdditionalGridConnectionPointInformationCard || false)
    );
  }

  ngOnInit(): void {
    combineLatest([this.dataShareService.uiAdaptions$, this.modelState$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([adaptions]) => {
        const conditionsCheckboxAnchor = this.conditionsCheckboxUIAdaptionName;
        const checkboxAdaption = conditionsCheckboxAnchor
          ? adaptions.find((adaption) => adaption.anchor === conditionsCheckboxAnchor)
          : null;
        this.updateCustomConditionsCheckboxValidation(checkboxAdaption);
      });

    this.canUpdate$ = of(true);
    this.disableAll$ = of(false);

    this.formGroup
      .get('state')
      .valueChanges.pipe(
        takeUntil(this.compUnsubs$),
        startWith(this.formGroup.get('state').value),
        tap((x) => {
          const isInstallerUser = this.authorizationService.hasOneOfThisRoles([
            'ROLE_INSTALLATEUR',
            'ROLE_ADMIN_INSTALLATEUR'
          ]);

          const isEditorUser = this.authorizationService.hasOneOfThisRoles(['ROLE_EDITOR_GRIDREGISTRATIONS']);
          const canEditAsInstaller =
            isInstallerUser &&
            [GridRegistrationStatusEnum.Incomplete, GridRegistrationStatusEnum.FeedbackNeeded].includes(
              x as CommonStatusEnum
            );
          const canEditAsEditor =
            isEditorUser &&
            [GridRegistrationStatusEnum.Requested, GridRegistrationStatusEnum.Approved].includes(
              x as CommonStatusEnum
            );

          const canEditAsAnonUser = [
            GridRegistrationStatusEnum.Incomplete,
            GridRegistrationStatusEnum.FeedbackNeeded
          ].includes(x as CommonStatusEnum);

          const canEditAsKeyUser = this.authorizationService.hasOneOfThisRoles([
            'ROLE_KEYUSER_GRIDREGISTRATION'
          ]);
          const canEdit = canEditAsEditor || canEditAsInstaller || canEditAsKeyUser || canEditAsAnonUser;

          if (!canEdit) {
            this.disableFields();
          } else {
            this.enableFields();
          }
        })
      )
      .subscribe();

    // Immer wenn differingInvoiceContact sich ändert soll
    // bei änderung auf false
    // invoiceContact geleert werden
    // bei Änderung auf true
    // nichts passieren :)
    this.formGroup
      .get('differingInvoiceContact')
      .valueChanges.pipe(
        tap((x) => {
          if (x) {
            // TODO?
          } else {
            this.invoiceContact.setValue(ContactDataDto.empty());
          }
        })
      )
      .subscribe();

    this.selectedCompanyLocation$ = this.formGroup.get('connectionObject.orgUnitDesc').valueChanges.pipe(
      takeUntil(this.compUnsubs$),
      startWith(this.formGroup.get('connectionObject.orgUnitDesc').value),
      combineLatestWith(this.dataShareService.companyLocations$),
      filter(([orgUnitDesc, companyLocations]) => !!orgUnitDesc && !!companyLocations),
      map(([orgUnitDesc, companyLocations]) => {
        return companyLocations.find((x) => x.external_id === orgUnitDesc);
      }),
      // after we selected the companyLocation we want to patch the id into the form
      tap((x) => console.debug('tapping into selectedCompanyLocation', x)),
      tap((val) => this.formGroup.patchValue({ companyLocation: { id: val.id } }, { emitEvent: false }))
    );

    // we need to subscribe here or otherwise the tap never gets triggered
    this.selectedCompanyLocation$.subscribe();
  }

  getModelFromForm(): GridRegistrationModel {
    // we need to map a few properties to keep the model consisten
    const response = this.formGroup.value;
    return {
      ...this.originalModel,
      ...response
    };
  }

  loadErrorsList() {
    console.debug('executed loadErrorList');
  }

  /**
   * Dies wird bei der initialen Befüllung des Forms aufgerufen.
   * @param model
   */
  loadFormWithModel(model: GridRegistrationModel) {
    console.debug('executed loadFormWithModel: ', model);
    this.updateFormWithModelInternal(model);
  }

  /**
   * Und das hier z.B. nachdem etwas gespeichert wurde
   * @param model
   */
  updateFormWithModel(model: GridRegistrationModel) {
    console.debug('executed updateFormWithModel: ', model);
    this.updateFormWithModelInternal(model);
  }

  private updateFormWithModelInternal(model: GridRegistrationModel) {
    this.isAdminUser = this.authorizationService.hasRole('ROLE_EDITOR_GRIDREGISTRATIONS');

    // we want to store the "old" model to patch it later
    this.originalModel = model;

    // Initialisieren der CodeListValues, wenn Netzanmeldungstyp Misc ist
    if (this.isMiscRegistration) {
      this.initMiscRegistrationTypes$();
    }

    // Stellen Sie sicher, dass alle FormArrays und FormGroups initialisiert sind
    console.debug('updating model', model);

    this.consumers.clear();
    model.consumers.forEach((consumer) => {
      const consumerForm = this.createFormsGridRegistrationService.createConsumerFormV2(consumer);
      this.consumers.push(consumerForm);
    });

    this.uploads.clear();
    model.uploads.forEach((upload) => {
      const uploadFormGroup = this.createFormService.createUploadForm(upload);
      this.uploads.push(uploadFormGroup);
    });

    this.existingFeeders.clear();
    model.existingFeeders.forEach((existingFeeder) => {
      const existingFeederFormGroup =
        this.createFormsGridRegistrationService.createExistingFeederForm(existingFeeder);
      this.existingFeeders.push(existingFeederFormGroup);
    });

    // Laden Sie jetzt das Modell in das Formular
    this.formGroup.patchValue(model);
    this.modelState$.next(model?.state as CommonStatusEnum);
  }

  private updateCustomConditionsCheckboxValidation(checkboxAdaption: UiAdaptionsModel | null): void {
    if (
      !this.customConditionsCheckbox.disabled &&
      checkboxAdaption &&
      (checkboxAdaption.visibility === 'show' || checkboxAdaption.visibility === 'true')
    ) {
      this.customConditionsCheckbox.setValidators(this.validatorsService.checkboxRequiredValidator());
    } else {
      this.customConditionsCheckbox.clearValidators();
    }
    if (
      this.originalModel?.state !== CommonStatusEnum.Incomplete &&
      this.originalModel?.state !== CommonStatusEnum.FeedbackNeeded
    ) {
      this.customConditionsCheckbox.clearValidators();
      this.customConditionsCheckbox.setValue(true);
    }
    this.customConditionsCheckbox.updateValueAndValidity();
  }

  private disableFormGroup(formGroup: FormGroup | FormArray) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.controls[key];

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.disableFormGroup(control);
      } else {
        control.disable();
      }
    });
  }

  /**
   * Hier wird von der enthaltenden Page angefragt, ob das Formular valide ist
   * Wird z.B. vor Abschicken geprüft.
   */
  isValid() {
    this.formGroup.markAllAsTouched();
    this.customConditionsCheckbox.markAllAsTouched();
    return (
      (this.formGroup.valid || this.formGroup.disabled) &&
      this.customConditionsCheckbox.valid &&
      this.isConnectionObjectAreaAvailabilityValid()
    );
  }

  // Code für sonstige Verbraucher

  miscRegistrationTypes$: Observable<CodeListValue[]> = of([]);
  connectionObjectData$: Observable<ConnectionObjectFormV2['value']>;

  initMiscRegistrationTypes$() {
    this.miscRegistrationTypes$ = this.dataShareService.currentTenant$.pipe(
      takeUntil(this.compUnsubs$),
      switchMap((tenant) => {
        return this.metaService.codeListValues$('grid-registrations.misc-types', tenant.id);
      })
    );
  }

  get isMiscRegistration() {
    return this.gridRegistrationType === GridRegistrationTypeEnum.Misc;
  }

  resetDifferingInvoiceContactForm(checked: boolean) {
    if (!checked) {
      for (const control of Object.values(this.invoiceContact.controls)) {
        control.clearValidators();
        control.patchValue(null);
      }
    }
  }

  private initConnectionObjectData$() {
    this.connectionObjectData$ = this.formGroup.controls.connectionObject.valueChanges.pipe(
      takeUntil(this.compUnsubs$),
      startWith(this.formGroup.controls.connectionObject.value),
      shareReplay(1)
    );
  }
}
